import Skeleton from "@ui-kit/feedback/Skeleton/Skeleton.web";
import MembershipModal from "@/components/organisms/purchases/modals/MembershipModal/MembershipModal";
import { formatRelativeStaticPath } from "@shared/utils/images/utils";
import { useDisclosure } from "@shared/hooks/useDisclosure";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import styled from "@emotion/styled";
import { useTranslation } from "@shared/utils/internationalization";
import React from "react";
import CardImage from "@ui-application/media/CardImage/CardImage.web";
import { getPriceRange } from "@shared/modules/financials/utils/price-utils";
import { NestedPublicVenueSerializer } from "@app-contexts/public/features/event-detail/data/types/events";
import { PublicMembershipGroupSerializer } from "@shared/modules/memberships/types";

const ContentContainer = styled(Box)`
  flex-grow: 2;
  width: 100%;
  height: 100%;
  justify-content: center;
  padding: 2px;

  :hover {
    img {
      opacity: 0.5;
    }
  }
`;

const MembershipNameText = styled(Text)`
  text-overflow: ellipsis;
  color: var(--chakra-colors-fg);
  -webkit-box-orient: vertical;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  overflow: hidden;
  white-space: normal;
`;

const RectangularContainer = styled(Flex)`
  border-radius: 10px;
  overflow: hidden;
  width: 100%;
  height: 100%;
  display: block;
  aspect-ratio: 2 / 1;
`;

export interface AttractionMembershipItemProps {
  membershipGroup: PublicMembershipGroupSerializer;
  venue: NestedPublicVenueSerializer;
  isLoading: boolean;
}

const AttractionMembershipItem: React.FC<AttractionMembershipItemProps> = ({
  membershipGroup,
  venue,
  isLoading,
}) => {
  const { t } = useTranslation("purchases");
  const defaultImage = formatRelativeStaticPath("images/default-banner.png");
  const skeletonText = "skeletonText";

  const cardImage = membershipGroup?.image;

  // attraction modal variables
  const {
    isOpen: isOpenMembershipModal,
    onOpen: onOpenMembershipModal,
    onClose: onCloseMembershipModal,
  } = useDisclosure();

  const membershipName = membershipGroup?.name ?? t("membership");

  const priceRange =
    membershipGroup?.membership_levels &&
    membershipGroup.membership_levels.length > 0
      ? getPriceRange({
          items: membershipGroup.membership_levels,
          allInPricing: Boolean(venue.all_in_pricing_is_enabled),
          venueCurrency: venue.currency,
        })
      : null;

  return (
    <>
      <MembershipModal
        isOpen={isOpenMembershipModal}
        onClose={onCloseMembershipModal}
        selectedAttractionMembership={membershipGroup}
        venue={venue}
      />
      <ContentContainer
        onClick={() => {
          if (!isLoading) {
            onOpenMembershipModal();
          }
        }}
      >
        <Flex
          cursor="pointer"
          direction="column"
          width="100%"
          maxWidth="100%"
          tabIndex={0}
          as="button"
          textAlign="left"
          aria-label={
            priceRange
              ? t(
                  "View details for {{membershipName}} priced at {{priceRange}}",
                  {
                    membershipName,
                    priceRange,
                  }
                )
              : t("View details for {{membershipName}}", {
                  membershipName,
                })
          }
          _focusVisible={{
            boxShadow: "outline",
            outlineOffset: "2px",
          }}
        >
          <Skeleton borderRadius="md" isLoaded={!isLoading}>
            <RectangularContainer>
              <CardImage
                src={cardImage ?? defaultImage}
                alt={t("Membership image")}
                height={190}
                width={380}
              />
            </RectangularContainer>
          </Skeleton>
          <Skeleton isLoaded={!isLoading} mt="xs">
            <MembershipNameText
              fontWeight="bold"
              fontSize={{ base: "sm", lg: "xl" }}
            >
              {membershipGroup?.name ?? skeletonText}
            </MembershipNameText>
          </Skeleton>
          <Skeleton isLoaded={!isLoading} mt="2xs" width="fit-content">
            <Text
              hidden={!venue.city}
              color="gray.600"
              fontSize={{ base: "xs", lg: "lg" }}
            >
              {venue.city ?? skeletonText}
            </Text>
          </Skeleton>
          <Skeleton isLoaded={!isLoading} mt="lg">
            {membershipGroup?.membership_levels &&
              membershipGroup.membership_levels.length > 0 && (
                <Text fontSize={{ base: "xs", lg: "lg" }}>
                  {getPriceRange({
                    items: membershipGroup.membership_levels,
                    allInPricing: Boolean(venue.all_in_pricing_is_enabled),
                    venueCurrency: venue.currency,
                  }) ?? skeletonText}
                </Text>
              )}
          </Skeleton>
        </Flex>
      </ContentContainer>
    </>
  );
};

export default AttractionMembershipItem;
